import { ActivityEventProcessType } from '@warebee/shared/import-converter';

export const pickingKpis /*: WarehouseKPI[] */ = [
  {
    id: 'uph_picking',
    processType: [ActivityEventProcessType.PICKING],
    category: 'process',
    title: 'Units Per Hour (UPH)',
    description:
      'Direct picking productivity including travel and pick time (UPH)',
    unit: 'units/hr',
    formula: '(SUM(picked_units) / SUM(pick_time_minutes)) * 60',
    tags: ['iso-22400:PEE', 'labor', 'picking', 'productivity'],
    isPrimary: true,
    benchmark: {
      standards: {
        site: {
          target: 120,
          warning: { lower: 100 },
          critical: { lower: 80 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 80 },
            { label: 'Warning', min: 80.01, max: 100 },
            { label: 'Target', min: 100.01, max: 120 },
          ],
        },
      ],
      metadata: { source: 'legacy migration' },
    },
    trendWindow: 'P28D',
    forecastHorizon: 'P7D',
    drillDimensions: ['zone', 'shift', 'picker_id'],
    alertTopic: 'warehouse.alerts.process.uph_picking',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'influenced_by',
        strength: 0.8,
        lag: 'PT30M',
        description:
          'Picking UPH is directly affected by replenishment efficiency. Low replenishment rates can cause stock-outs at pick faces, leading to reduced picking speeds.',
      },
      {
        relatedKpiId: 'avg_lines_per_order',
        relationshipType: 'influenced_by',
        strength: 0.7,
        description:
          'Higher line density per order improves picking efficiency through better picking route optimization.',
      },
      {
        relatedKpiId: 'average_distance',
        relationshipType: 'opposing',
        strength: 0.6,
        description:
          'Travel distance has an inverse relationship with UPH - longer travel distances reduce units picked per hour.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['bar', 'gauge'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'shift', 'picker_id'],
      annotateWith: {
        eventTypes: ['layout_change', 'system_outage', 'training'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'includeTravel',
        label: 'Include Travel Time',
        description:
          'Whether to include travel time in productivity calculation',
        type: 'boolean',
        defaultValue: true,
        userConfigurable: true,
        uiComponent: 'checkbox',
      },
      {
        name: 'pickingMethod',
        label: 'Picking Method',
        description: 'Filter by specific picking method',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'zone_pick',
          'batch_pick',
          'wave_pick',
          'cluster_pick',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
    recommendedActions: [
      {
        kpiId: 'uph_picking',
        recommendedAction:
          'Investigate root causes of low picking productivity',
        tools: [
          {
            id: 'root-cause',
            name: 'Root Cause Analysis',
            description:
              'Analyze underlying issues affecting picking performance',
            category: 'rootCauseAnalysis',
            link: '/tools/root-cause',
          },
        ],
        steps: [
          'Check for recent replenishment delays',
          'Review staff allocation and shift patterns',
          'Analyze pick path optimization',
        ],
        priority: 'high',
      },
      {
        kpiId: 'uph_picking',
        recommendedAction: 'Optimize picking routes and slotting',
        tools: [
          {
            id: 'slotting-tool',
            name: 'Slotting Optimization',
            description: 'Optimize item slotting to reduce travel distance',
            category: 'Optimize',
            link: '/tools/slotting',
          },
        ],
        steps: [
          'Run slotting simulation for high-velocity items',
          'Adjust pick face assignments',
        ],
        priority: 'medium',
      },
    ],
  },
  {
    id: 'picking_accuracy',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Picking Accuracy',
    description:
      'Percentage of items picked correctly (right item, quantity, condition)',
    unit: '%',
    formula: '(1 - (COUNT(picking_exceptions) / COUNT(picked_items))) * 100',
    tags: ['picking', 'quality', 'iso-22400:QAS'],
    isPrimary: true,
    benchmark: {
      standards: {
        site: {
          target: 99.8,
          warning: { lower: 99 },
          critical: { lower: 98 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 98 },
            { label: 'Warning', min: 98.01, max: 99 },
            { label: 'Target', min: 99.01, max: 99.8 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'item_category'],
    alertTopic: 'warehouse.alerts.process.picking_accuracy',
    relationships: [
      {
        relatedKpiId: 'uph_picking',
        relationshipType: 'opposing',
        strength: 0.6,
        description:
          'There is often a trade-off between picking speed and accuracy. Pushing for higher UPH can reduce accuracy.',
      },
      {
        relatedKpiId: 'avg_weight_per_line',
        relationshipType: 'influenced_by',
        strength: 0.5,
        description:
          'Heavier items tend to be picked with greater care, affecting accuracy rates.',
      },
      {
        relatedKpiId: 'avg_volume_per_line',
        relationshipType: 'influenced_by',
        strength: 0.4,
        description:
          'Larger items are more visible and thus less likely to be picked incorrectly.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['gauge'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['picker_id', 'zone', 'item_category'],
      annotateWith: {
        eventTypes: ['training', 'system_update'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'errorType',
        label: 'Error Type',
        description: 'Filter by specific error type',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'wrong_item',
          'wrong_quantity',
          'damaged',
          'missed_pick',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'average_picking_time',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Picking Time',
    description: 'Average time to pick an order',
    unit: 'min',
    formula: 'AVG(picking_time)',
    tags: ['picking', 'time'],
    benchmark: {
      standards: {
        site: {
          target: 2,
          warning: { upper: 3 },
          critical: { upper: 5 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 2 },
            { label: 'Warning', min: 2.01, max: 3 },
            { label: 'Target', min: 3.01, max: 5 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.average_picking_time',
  },
  {
    id: 'average_distance',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Distance',
    description: 'Average distance traveled per order',
    unit: 'm',
    formula: 'AVG(distance_traveled)',
    tags: ['distance', 'picking'],
    benchmark: {
      standards: {
        site: {
          target: 3,
          warning: { upper: 5 },
          critical: { upper: 8 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 3 },
            { label: 'Warning', min: 3.01, max: 5 },
            { label: 'Target', min: 5.01, max: 8 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.average_distance',
  },
  {
    id: 'picked_early',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Picked Early',
    description: 'Number of orders picked before the scheduled pick time',
    unit: 'orders',
    formula: 'COUNT(orders WHERE pick_time < simulated_pick_time)',
    tags: ['picking', 'timeliness', 'early'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 20 },
          critical: { upper: 30 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 30, max: 1000 },
            { label: 'Warning', min: 20, max: 29.99 },
            { label: 'Target', min: 0, max: 19.99 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    forecastHorizon: 'P7D',
    drillDimensions: ['picker_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.picked_early',
  },
  {
    id: 'picked_late',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Picked Late',
    description: 'Number of orders picked after the scheduled pick time',
    unit: 'orders',
    formula: 'COUNT(orders WHERE pick_time > simulated_pick_time)',
    tags: ['picking', 'timeliness', 'late'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 10 },
          critical: { upper: 20 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 20, max: 1000 },
            { label: 'Warning', min: 10, max: 19.99 },
            { label: 'Target', min: 0, max: 9.99 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    forecastHorizon: 'P7D',
    drillDimensions: ['picker_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.picked_late',
  },
  {
    id: 'order_line_distribution_min',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Order Line Distribution (Min)',
    description: 'Minimum number of lines per order',
    unit: 'lines',
    formula: 'MIN(lines_per_order)',
    tags: ['picking', 'order', 'profiling'],
    benchmark: {
      standards: {
        site: {
          target: 1,
          warning: { lower: 1 },
          critical: { lower: 1 },
          basis: 'Order profiling standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 1 },
            { label: 'Warning', min: 1.01, max: 1 },
            { label: 'Target', min: 1.01, max: 5 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.order_line_distribution_min',
  },
  {
    id: 'order_line_distribution_max',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Order Line Distribution (Max)',
    description: 'Maximum number of lines per order',
    unit: 'lines',
    formula: 'MAX(lines_per_order)',
    tags: ['picking', 'order', 'profiling'],
    benchmark: {
      standards: {
        site: {
          target: 20,
          warning: { upper: 30 },
          critical: { upper: 40 },
          basis: 'Order profiling standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.order_line_distribution_max',
  },
  {
    id: 'order_line_distribution_avg',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Order Line Distribution (Avg)',
    description: 'Average number of lines per order',
    unit: 'lines',
    formula: 'AVG(lines_per_order)',
    tags: ['picking', 'order', 'profiling'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 8 },
          critical: { upper: 12 },
          basis: 'Order profiling standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 8 },
            { label: 'Critical', min: 8.01, max: 20 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.order_line_distribution_avg',
  },
  {
    id: 'avg_lines_per_picklist',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Picklist',
    description: 'Average number of lines per picklist',
    unit: 'lines/picklist',
    formula: 'AVG(lines_per_picklist)',
    tags: ['picking', 'picklist', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { lower: 8, upper: 15 },
          critical: { lower: 5, upper: 20 },
          basis: 'Picklist optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical Low', min: 0, max: 5 },
            { label: 'Warning Low', min: 5.01, max: 8 },
            { label: 'Target', min: 8.01, max: 15 },
            { label: 'Warning High', min: 15.01, max: 20 },
            { label: 'Critical High', min: 20.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.avg_lines_per_picklist',
  },
  {
    id: 'avg_lines_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Order',
    description: 'Average number of lines per order',
    unit: 'lines/order',
    formula: 'AVG(lines_per_order)',
    tags: ['picking', 'order', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { lower: 3, upper: 8 },
          critical: { lower: 1, upper: 12 },
          basis: 'Order profile standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical Low', min: 0, max: 1 },
            { label: 'Warning Low', min: 1.01, max: 3 },
            { label: 'Target', min: 3.01, max: 8 },
            { label: 'Warning High', min: 8.01, max: 12 },
            { label: 'Critical High', min: 12.01, max: 30 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.avg_lines_per_order',
  },
  {
    id: 'avg_distance_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Distance per Order',
    description: 'Average distance traveled to fulfill an order',
    unit: 'm/order',
    formula: 'SUM(distance_traveled) / COUNT(orders)',
    tags: ['picking', 'distance', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 50,
          warning: { upper: 75 },
          critical: { upper: 100 },
          basis: 'Travel optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 50 },
            { label: 'Warning', min: 50.01, max: 75 },
            { label: 'Critical', min: 75.01, max: 200 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'zone', 'picker_id'],
    alertTopic: 'warehouse.alerts.process.avg_distance_per_order',
  },
  {
    id: 'avg_distance_per_picklist',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Distance per Picklist',
    description: 'Average distance traveled to complete a picklist',
    unit: 'm/picklist',
    formula: 'SUM(distance_traveled) / COUNT(picklists)',
    tags: ['picking', 'distance', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 100,
          warning: { upper: 150 },
          critical: { upper: 200 },
          basis: 'Travel optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 100 },
            { label: 'Warning', min: 100.01, max: 150 },
            { label: 'Critical', min: 150.01, max: 300 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.avg_distance_per_picklist',
  },
  {
    id: 'avg_time_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Time per Order',
    description: 'Average time to complete an order',
    unit: 'min/order',
    formula: 'SUM(picking_time_minutes) / COUNT(orders)',
    tags: ['picking', 'time', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 8,
          warning: { upper: 12 },
          critical: { upper: 15 },
          basis: 'Time efficiency standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 8 },
            { label: 'Warning', min: 8.01, max: 12 },
            { label: 'Critical', min: 12.01, max: 30 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'picker_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.avg_time_per_order',
  },
  {
    id: 'avg_time_per_picklist',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Average Time per Picklist',
    description: 'Average time to complete a picklist',
    unit: 'min/picklist',
    formula: 'SUM(picking_time_minutes) / COUNT(picklists)',
    tags: ['picking', 'time', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 20 },
          critical: { upper: 30 },
          basis: 'Time efficiency standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 20 },
            { label: 'Critical', min: 20.01, max: 40 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.avg_time_per_picklist',
  },
  {
    id: 'pick_rate',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Pick Rate',
    description: 'Number of picks per hour',
    unit: 'picks/hr',
    formula: '(COUNT(picks) / SUM(pick_time_minutes)) * 60',
    tags: ['picking', 'productivity', 'rate'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 60 },
          critical: { lower: 40 },
          basis: 'Productivity standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 40 },
            { label: 'Warning', min: 40.01, max: 60 },
            { label: 'Target', min: 60.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.pick_rate',
  },
  {
    id: 'lines_per_hour',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Lines Per Hour (LPH)',
    description: 'Number of order lines processed per hour',
    unit: 'lines/hr',
    formula: '(COUNT(order_lines) / SUM(processing_time_minutes)) * 60',
    tags: ['picking', 'productivity', 'lines'],
    benchmark: {
      standards: {
        site: {
          target: 60,
          warning: { lower: 45 },
          critical: { lower: 30 },
          basis: 'Productivity standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 30 },
            { label: 'Warning', min: 30.01, max: 45 },
            { label: 'Target', min: 45.01, max: 60 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.lines_per_hour',
  },
  {
    id: 'orders_per_hour',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Orders Per Hour (OPH)',
    description: 'Number of orders processed per hour',
    unit: 'orders/hr',
    formula: '(COUNT(orders) / SUM(processing_time_minutes)) * 60',
    tags: ['picking', 'productivity', 'orders'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { lower: 10 },
          critical: { lower: 5 },
          basis: 'Productivity standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 10 },
            { label: 'Target', min: 10.01, max: 15 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['picker_id', 'zone', 'shift'],
    alertTopic: 'warehouse.alerts.process.orders_per_hour',
  },
  {
    id: 'avg_weight_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Avg. Weight per Line',
    description: 'Average weight per order line',
    unit: 'kg',
    formula: 'AVG(line_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 2,
          warning: { upper: 3 },
          critical: { upper: 5 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 2 },
            { label: 'Warning', min: 2.01, max: 3 },
            { label: 'Critical', min: 3.01, max: 10 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone', 'picker_id'],
    alertTopic: 'warehouse.alerts.process.avg_weight_per_line',
  },
  {
    id: 'min_weight_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Min. Weight per Line',
    description: 'Minimum weight per order line',
    unit: 'kg',
    formula: 'MIN(line_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.1,
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [{ label: 'Target', min: 0, max: 0.1 }],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone'],
    alertTopic: 'warehouse.alerts.process.min_weight_per_line',
  },
  {
    id: 'max_weight_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Max. Weight per Line',
    description: 'Maximum weight per order line',
    unit: 'kg',
    formula: 'MAX(line_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 15 },
          critical: { upper: 25 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 15 },
            { label: 'Critical', min: 15.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone'],
    alertTopic: 'warehouse.alerts.process.max_weight_per_line',
  },
  {
    id: 'avg_weight_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Avg. Weight per Order',
    description: 'Average weight per order',
    unit: 'kg',
    formula: 'AVG(order_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 15 },
          critical: { upper: 25 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 15 },
            { label: 'Critical', min: 15.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.avg_weight_per_order',
  },
  {
    id: 'min_weight_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Min. Weight per Order',
    description: 'Minimum weight per order',
    unit: 'kg',
    formula: 'MIN(order_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.5,
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [{ label: 'Target', min: 0, max: 0.5 }],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.process.min_weight_per_order',
  },
  {
    id: 'max_weight_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Max. Weight per Order',
    description: 'Maximum weight per order',
    unit: 'kg',
    formula: 'MAX(order_weight)',
    tags: ['picking', 'weight', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { upper: 50 },
          critical: { upper: 75 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 30 },
            { label: 'Warning', min: 30.01, max: 50 },
            { label: 'Critical', min: 50.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.process.max_weight_per_order',
  },
  {
    id: 'avg_volume_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Avg. Volume per Line',
    description: 'Average volume (cube) per order line',
    unit: 'm³',
    formula: 'AVG(line_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.01,
          warning: { upper: 0.02 },
          critical: { upper: 0.05 },
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0.01 },
            { label: 'Warning', min: 0.0101, max: 0.02 },
            { label: 'Critical', min: 0.0201, max: 0.1 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone'],
    alertTopic: 'warehouse.alerts.process.avg_volume_per_line',
  },
  {
    id: 'min_volume_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Min. Volume per Line',
    description: 'Minimum volume (cube) per order line',
    unit: 'm³',
    formula: 'MIN(line_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.0001,
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [{ label: 'Target', min: 0, max: 0.0001 }],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone'],
    alertTopic: 'warehouse.alerts.process.min_volume_per_line',
  },
  {
    id: 'max_volume_per_line',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Max. Volume per Line',
    description: 'Maximum volume (cube) per order line',
    unit: 'm³',
    formula: 'MAX(line_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.05,
          warning: { upper: 0.1 },
          critical: { upper: 0.2 },
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0.05 },
            { label: 'Warning', min: 0.0501, max: 0.1 },
            { label: 'Critical', min: 0.1001, max: 0.5 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['item_category', 'zone'],
    alertTopic: 'warehouse.alerts.process.max_volume_per_line',
  },
  {
    id: 'avg_volume_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Avg. Volume per Order',
    description: 'Average volume (cube) per order',
    unit: 'm³',
    formula: 'AVG(order_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.05,
          warning: { upper: 0.1 },
          critical: { upper: 0.2 },
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0.05 },
            { label: 'Warning', min: 0.0501, max: 0.1 },
            { label: 'Critical', min: 0.1001, max: 0.5 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id', 'zone'],
    alertTopic: 'warehouse.alerts.process.avg_volume_per_order',
  },
  {
    id: 'min_volume_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Min. Volume per Order',
    description: 'Minimum volume (cube) per order',
    unit: 'm³',
    formula: 'MIN(order_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.001,
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [{ label: 'Target', min: 0, max: 0.001 }],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.process.min_volume_per_order',
  },
  {
    id: 'max_volume_per_order',
    processType: ActivityEventProcessType.PICKING,
    category: 'process',
    title: 'Max. Volume per Order',
    description: 'Maximum volume (cube) per order',
    unit: 'm³',
    formula: 'MAX(order_volume)',
    tags: ['picking', 'volume', 'cube', 'ergonomics'],
    benchmark: {
      standards: {
        site: {
          target: 0.2,
          warning: { upper: 0.5 },
          critical: { upper: 1 },
          basis: 'Space optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0.2 },
            { label: 'Warning', min: 0.2001, max: 0.5 },
            { label: 'Critical', min: 0.5001, max: 2 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['order_type', 'client_id'],
    alertTopic: 'warehouse.alerts.process.max_volume_per_order',
  },
  {
    id: 'overdue_outbound_by_ship_to',
    processType: [
      ActivityEventProcessType.DISPATCH,
      ActivityEventProcessType.SHIPPING,
    ],
    category: 'process',
    title: 'Overdue Outbound By Ship-To Party',
    description:
      'Number of overdue outbound delivery order items without goods issue by ship-to party',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE status = "overdue" AND goods_issue IS NULL) GROUP BY ship_to_party',
    tags: ['outbound', 'delivery', 'overdue', 'shipping'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 15 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 5 },
            { label: 'Critical', min: 6, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['ship_to_party', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.overdue_outbound_by_ship_to',
  },
  {
    id: 'blocked_outbound_by_time',
    processType: [
      ActivityEventProcessType.DISPATCH,
      ActivityEventProcessType.SHIPPING,
    ],
    category: 'process',
    title: 'Blocked Outbound By Planned Issue Time',
    description:
      'Number of blocked outbound delivery order items by planned goods issue time',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE status = "blocked") GROUP BY HOUR(planned_goods_issue_time)',
    tags: ['outbound', 'delivery', 'blocked', 'shipping'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 10 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 5 },
            { label: 'Critical', min: 6, max: 30 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['planned_issue_hour', 'block_reason', 'ship_to_party'],
    alertTopic: 'warehouse.alerts.process.blocked_outbound_by_time',
  },
  {
    id: 'outbound_without_pick_by_time',
    processType: [
      ActivityEventProcessType.PICKING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound Without Pick Tasks By Time',
    description:
      'Number of outbound delivery order items without pick warehouse tasks by planned goods issue time',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE pick_task_id IS NULL) GROUP BY HOUR(planned_goods_issue_time)',
    tags: ['outbound', 'delivery', 'picking', 'tasks'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 10 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 5 },
            { label: 'Critical', min: 6, max: 30 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['planned_issue_hour', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_without_pick_by_time',
  },
  {
    id: 'outbound_without_issue_by_time',
    processType: [
      ActivityEventProcessType.SHIPPING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound Without Goods Issue By Time',
    description:
      'Number of outbound delivery order items without goods issue by planned goods issue time',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE goods_issue IS NULL) GROUP BY HOUR(planned_goods_issue_time)',
    tags: ['outbound', 'delivery', 'shipping', 'goods issue'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 10 },
          critical: { upper: 20 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 10 },
            { label: 'Critical', min: 11, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['planned_issue_hour', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_without_issue_by_time',
  },
  {
    id: 'outbound_without_issue_by_ship_to',
    processType: [
      ActivityEventProcessType.SHIPPING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound Without Goods Issue By Ship-To',
    description:
      'Number of outbound delivery order items without goods issue by ship-to party',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE goods_issue IS NULL) GROUP BY ship_to_party',
    tags: ['outbound', 'delivery', 'shipping', 'goods issue'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 10 },
          critical: { upper: 20 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 10 },
            { label: 'Critical', min: 11, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['ship_to_party', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_without_issue_by_ship_to',
  },
  {
    id: 'outbound_by_issue_status',
    processType: [
      ActivityEventProcessType.SHIPPING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound By Goods Issue Status',
    description:
      'Number of outbound delivery order items by goods issue status',
    unit: 'items',
    formula: 'COUNT(outbound_items) GROUP BY goods_issue_status',
    tags: ['outbound', 'delivery', 'shipping', 'goods issue'],
    benchmark: {
      standards: {
        site: {
          target: 150,
          warning: { lower: 100 },
          critical: { lower: 75 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 75 },
            { label: 'Warning', min: 76, max: 100 },
            { label: 'Target', min: 101, max: 150 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['goods_issue_status', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_by_issue_status',
  },
  {
    id: 'outbound_with_issue_by_time',
    processType: [
      ActivityEventProcessType.SHIPPING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound With Goods Issue By Time',
    description:
      'Number of outbound delivery order items with goods issue by actual goods issue time',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE goods_issue IS NOT NULL) GROUP BY HOUR(actual_goods_issue_time)',
    tags: ['outbound', 'delivery', 'shipping', 'goods issue'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { lower: 20 },
          critical: { lower: 10 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 10 },
            { label: 'Warning', min: 11, max: 20 },
            { label: 'Target', min: 21, max: 30 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['actual_issue_hour', 'order_type', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_with_issue_by_time',
  },
  {
    id: 'outbound_incomplete_wave_by_time',
    processType: [
      ActivityEventProcessType.PICKING,
      ActivityEventProcessType.DISPATCH,
    ],
    category: 'process',
    title: 'Outbound With Incomplete Wave By Time',
    description:
      'Number of outbound delivery order items with incomplete wave assignments by planned goods issue time',
    unit: 'items',
    formula:
      'COUNT(outbound_items WHERE wave_status = "incomplete") GROUP BY HOUR(planned_goods_issue_time)',
    tags: ['outbound', 'delivery', 'picking', 'wave'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 10 },
          basis: 'Outbound performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 1, max: 5 },
            { label: 'Critical', min: 6, max: 20 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['planned_issue_hour', 'wave_id', 'priority'],
    alertTopic: 'warehouse.alerts.process.outbound_incomplete_wave_by_time',
  },
];

export default pickingKpis;
